import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  Target,
  Zap,
  Clock,
  TrendingUp,
  Shield,
  AlertTriangle,
  Rocket,
  Play,
  Pause,
  Settings2,
  ExternalLink,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { buildBondingCurveTradeTransaction } from "@/lib/solana-transactions";
import { useWallet as useSolanaWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";

interface SniperConfig {
  enabled: boolean;
  minLiquidity: number;
  minMarketCap: number;
  maxMarketCap: number;
  buyAmount: number;
  autoSell: boolean;
  takeProfitPercent: number;
  stopLossPercent: number;
  maxSlippage: number;
  antiRug: boolean;
}

export function TokenSniper() {
  const { connected, publicKey, balance } = useWallet();
  const { connection } = useConnection();
  const wallet = useSolanaWallet();
  const [config, setConfig] = useState<SniperConfig>({
    enabled: false,
    minLiquidity: 1,
    minMarketCap: 1000,
    maxMarketCap: 50000,
    buyAmount: 0.1,
    autoSell: true,
    takeProfitPercent: 100,
    stopLossPercent: 30,
    maxSlippage: 5,
    antiRug: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateBuyAmount = (value: number) => {
    if (value < 0.05) {
      setErrors(prev => ({ ...prev, buyAmount: 'Minimum 0.05 SOL' }));
      return false;
    }
    if (value > balance) {
      setErrors(prev => ({ ...prev, buyAmount: 'Insufficient balance' }));
      return false;
    }
    setErrors(prev => ({ ...prev, buyAmount: '' }));
    return true;
  };

  useEffect(() => {
    validateBuyAmount(config.buyAmount);
  }, [config.buyAmount, balance]);

  // Fetch new coins in real-time
  const { data: newCoins, isLoading } = useQuery({
    queryKey: ["new-coins-sniper"],
    queryFn: async () => {
      const thirtyMinutesAgo = Math.floor(Date.now() / 1000) - 1800;

      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .gte("created_at", thirtyMinutesAgo)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
    refetchInterval: 3000,
  });

  // Subscribe to new coin launches
  // Subscribe to new coin launches
  useEffect(() => {
    const channel = supabase
      .channel("new-launches")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "coins",
        },
        async (payload) => {
          const coin = payload.new as any;
          const meetsFilters =
            (coin.market_cap || 0) >= config.minMarketCap &&
            (coin.market_cap || 0) <= config.maxMarketCap &&
            (coin.liquidity || 0) >= config.minLiquidity;

          if (config.enabled && meetsFilters) {
            toast({
              title: "🚀 Sniping Token!",
              description: `Attempting to auto-buy ${coin.name}...`,
            });

            if (connected && publicKey && wallet.signTransaction && coin.mint_address) {
              try {
                const transaction = await buildBondingCurveTradeTransaction(connection, wallet, {
                  coinId: coin.id,
                  tradeType: 'buy',
                  amount: BigInt(Math.floor(config.buyAmount * 1_000_000_000)),
                  userWallet: publicKey,
                  tokenMintAddress: new PublicKey(coin.mint_address),
                  slippageTolerance: config.maxSlippage
                });

                const signedTx = await wallet.signTransaction(transaction);
                const signature = await connection.sendRawTransaction(signedTx.serialize());

                toast({
                  title: "Snipe Transaction Sent!",
                  description: `Signature: ${signature.slice(0, 8)}...`,
                  variant: "default",
                  className: "bg-green-500 text-white"
                });
              } catch (error) {
                console.error("Snipe failed:", error);
                toast({
                  title: "Snipe Failed",
                  description: error instanceof Error ? error.message : "Unknown error",
                  variant: "destructive"
                });
              }
            } else {
              toast({
                title: "Snipe Skipped",
                description: "Wallet not connected or Mint Address missing.",
                variant: "destructive"
              });
            }
          } else if (config.enabled) {
            toast({
              title: "New Token Detected",
              description: `${coin.name} (Skipped: Filters not met)`,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [config.enabled, config, connected, publicKey, wallet, connection]);

  if (!connected) {
    return (
      <Card className="p-6 text-center">
        <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-semibold mb-2">Token Sniper</h3>
        <p className="text-sm text-muted-foreground">
          Connect your wallet to use the token sniper
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sniper Status */}
      <Card className={cn(
        "p-4 border-2 transition-colors",
        config.enabled ? "border-success bg-success/5" : "border-border"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center",
              config.enabled ? "bg-success text-success-foreground" : "bg-secondary"
            )}>
              {config.enabled ? (
                <Zap className="w-5 h-5" />
              ) : (
                <Target className="w-5 h-5" />
              )}
            </div>
            <div>
              <h3 className="font-semibold">
                Token Sniper {config.enabled ? "Active" : "Inactive"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {config.enabled
                  ? "Watching for new launches..."
                  : "Enable to auto-snipe new tokens"}
              </p>
            </div>
          </div>

          <Button
            variant={config.enabled ? "destructive" : "default"}
            onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
            className="gap-2"
            disabled={config.enabled ? false : !!errors.buyAmount}
          >
            {config.enabled ? (
              <>
                <Pause className="w-4 h-4" />
                Stop
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Start
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Sniper Settings */}
      <Card>
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Sniper Configuration</h3>
        </div>

        <div className="p-4 space-y-5">
          {/* Buy Amount */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Buy Amount: {config.buyAmount} SOL
            </label>
            <Slider
              value={[config.buyAmount]}
              onValueChange={([v]) => setConfig(prev => ({ ...prev, buyAmount: v }))}
              min={0.05}
              max={Math.min(balance, 5)}
              step={0.05}
              aria-label="Buy amount slider"
              aria-invalid={!!errors.buyAmount}
              aria-describedby={errors.buyAmount ? "buyAmount-error" : undefined}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0.05 SOL</span>
              <span>Balance: {balance.toFixed(2)} SOL</span>
            </div>
            {errors.buyAmount !== undefined && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${errors.buyAmount ? 'text-destructive' : 'text-success'}`}>
                {errors.buyAmount ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                {errors.buyAmount || 'Valid'}
              </p>
            )}
          </div>

          {/* Market Cap Range Filter */}
           <div>
             <label className="text-sm text-muted-foreground mb-2 block">
               Market Cap Range: ${config.minMarketCap.toLocaleString()} - ${config.maxMarketCap.toLocaleString()}
             </label>
             <div className="space-y-2">
               <Slider
                 value={[config.minMarketCap]}
                 onValueChange={([v]) => setConfig(prev => ({ ...prev, minMarketCap: Math.min(v, config.maxMarketCap - 1000) }))}
                 min={100}
                 max={config.maxMarketCap - 1000}
                 step={100}
                 aria-label="Minimum market cap slider"
               />
               <Slider
                 value={[config.maxMarketCap]}
                 onValueChange={([v]) => setConfig(prev => ({ ...prev, maxMarketCap: Math.max(v, config.minMarketCap + 1000) }))}
                 min={config.minMarketCap + 1000}
                 max={1000000}
                 step={1000}
                 aria-label="Maximum market cap slider"
               />
             </div>
           </div>

          {/* Min Liquidity */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Min Liquidity: {config.minLiquidity} SOL
            </label>
            <Slider
              value={[config.minLiquidity]}
              onValueChange={([v]) => setConfig(prev => ({ ...prev, minLiquidity: v }))}
              min={0.5}
              max={50}
              step={0.5}
              aria-label="Minimum liquidity slider"
            />
          </div>

          {/* Max Slippage */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Max Slippage: {config.maxSlippage}%
            </label>
            <Slider
              value={[config.maxSlippage]}
              onValueChange={([v]) => setConfig(prev => ({ ...prev, maxSlippage: v }))}
              min={1}
              max={20}
              step={0.5}
              aria-label="Maximum slippage slider"
            />
          </div>

          {/* Auto Sell Settings */}
          <div className="p-3 rounded-lg bg-secondary/50 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Auto Sell</span>
              <Switch
                checked={config.autoSell}
                onCheckedChange={(v) => setConfig(prev => ({ ...prev, autoSell: v }))}
              />
            </div>

            {config.autoSell && (
              <>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Take Profit: +{config.takeProfitPercent}%
                  </label>
                  <Slider
                    value={[config.takeProfitPercent]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, takeProfitPercent: v }))}
                    min={20}
                    max={500}
                    step={10}
                    aria-label="Take profit percentage slider"
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Stop Loss: -{config.stopLossPercent}%
                  </label>
                  <Slider
                    value={[config.stopLossPercent]}
                    onValueChange={([v]) => setConfig(prev => ({ ...prev, stopLossPercent: v }))}
                    min={10}
                    max={90}
                    step={5}
                    aria-label="Stop loss percentage slider"
                  />
                </div>
              </>
            )}
          </div>

          {/* Anti-Rug Protection */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Anti-Rug Protection</span>
            </div>
            <Switch
              checked={config.antiRug}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, antiRug: v }))}
            />
          </div>

          {/* Warning */}
          <div className="p-3 rounded-lg bg-warning/10 border border-warning/20 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-warning">
              Sniping new tokens is extremely risky. Many new tokens are scams or rugs.
              Only snipe with funds you can afford to lose completely.
            </p>
          </div>
        </div>
      </Card>

      {/* New Launches */}
      <Card>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Recent Launches</h3>
          </div>
          <Badge variant="secondary">
            <Clock className="w-3 h-3 mr-1" />
            Last 30m
          </Badge>
        </div>

        <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-3 flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-12" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                  <div className="flex items-center gap-3 text-xs mt-1">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-12" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            ))
          ) : newCoins && newCoins.length > 0 ? (
            newCoins.map((coin) => {
              const launchTime = coin.created_at
                ? formatDistanceToNow(new Date(coin.created_at * 1000), { addSuffix: true })
                : "just now";

              const meetsFilters =
                (coin.market_cap || 0) >= config.minMarketCap &&
                (coin.market_cap || 0) <= config.maxMarketCap &&
                (coin.liquidity || 0) >= config.minLiquidity;

              return (
                <Link
                  key={coin.id}
                  to={`/coin/${coin.id}`}
                  className={cn(
                    "p-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors",
                    meetsFilters && "bg-success/5"
                  )}
                >
                  <img
                    src={coin.image || "/placeholder.svg"}
                    alt={coin.name}
                    className="w-10 h-10 rounded-full bg-secondary"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{coin.name}</span>
                      <Badge variant="outline" className="text-xs">
                        ${coin.ticker}
                      </Badge>
                      {meetsFilters && (
                        <Badge variant="default" className="text-xs bg-success">
                          <Target className="w-3 h-3 mr-1" />
                          Match
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{launchTime}</span>
                      <span>MC: ${(coin.market_cap || 0).toLocaleString()}</span>
                      <span>Liq: {coin.liquidity?.toFixed(1) || 0} SOL</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {coin.price_change_24h && (
                      <Badge
                        variant={coin.price_change_24h >= 0 ? "default" : "destructive"}
                        className="font-mono"
                      >
                        {coin.price_change_24h >= 0 ? "+" : ""}
                        {coin.price_change_24h.toFixed(0)}%
                      </Badge>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </Link>
              );
            })
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <Rocket className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No recent launches</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
